import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { SidebarTabTypeEnum } from '../enums';
import { SearchService } from 'src/app/results/search/services/search.service';
import { LocationService } from '../../results/sidebar/services/location.service';
import { CONTINENT_SECTIONS } from '../constants';
import { FeatureFlagService } from './feature-flag.service';
import { SavedSearchCreateUpdateResponse } from '../dto';
import { RequestBodyCompany, RequestBodyContact } from '../../core/models';
import { SearchModelForm } from '../../results/search/services/SearchModelForm';

@Injectable({
    providedIn: 'root'
})
export class RequestBodyService {
    requestContactsBody: Subject<RequestBodyContact> = new Subject<RequestBodyContact>();
    requestCompaniesBody: Subject<RequestBodyCompany> = new Subject<RequestBodyCompany>();
    saveSearchCompany: Subject<SavedSearchCreateUpdateResponse<RequestBodyCompany>> = new Subject<SavedSearchCreateUpdateResponse<RequestBodyCompany>>();
    saveSearchContact: Subject<SavedSearchCreateUpdateResponse<RequestBodyContact>> = new Subject<SavedSearchCreateUpdateResponse<RequestBodyContact>>();
    revealMatchsId = new Subject();
    revealCompanyMatchsId = new Subject();
    isValid = new BehaviorSubject(false);
    isContactLocationValid = new BehaviorSubject(false);
    selectedTab = new BehaviorSubject("/results/search");
    onShortChange = new Subject();
    universalFilterChanged: Subject<SearchModelForm | any> = new Subject<SearchModelForm | any>();
    clearSearchSidebar = new Subject<any>();
    newSearchClicked = new Subject<any>();
    existingSearchClicked = new Subject<any>();
    isSearchClicked: boolean = false;
    isVIFilterValid = new BehaviorSubject(false);
    isVIFilterChangeFirstTime = new BehaviorSubject(true);
    private saveSearchVisitorIntel = new Subject();
    private clearSearchSidebarVI = new Subject<void>();
    private filtersEdited = new Subject<{ requestBody: any; edited: boolean }>();

    constructor(private searchService: SearchService, private locationService: LocationService) {

     }

    locationFilterUpdateSearchActions(data): void {
        this.locationService.resetSearchPanel();
        if (data && (!data.location_country_codes?.length && !data.query)) {
            const location_country_codes = this.generateLocationCountryCodes(data.is_international)
            this.searchService.searchModelData.location_country_codes = location_country_codes;
            this.searchService.savedSearchModelData.location_country_codes = location_country_codes;
        }
    }

    generateLocationCountryCodes(isInternational?: number | null): string[] {
        switch (isInternational) {
            case 0:
                return ['US'];
            case 1:
                const nonUSCodes = this.getCountryCodes(false);
                return nonUSCodes;
            case null:
            case undefined:
                const globalCodes = this.getCountryCodes(true);
                return globalCodes;
            default:
                return [];
        }
    }

    private getCountryCodes(includeUS: boolean) {
        return CONTINENT_SECTIONS
            // @ts-ignore
            .flatMap(section => {
                if ('continentName' in section) {
                    return section.countries.map(country => country.countryCode);
                } else {
                    return [section.countryCode];
                }
            })
            .filter(code => includeUS || code !== 'US');
    }

    changeContactBodyData(data) {
        this.requestContactsBody.next(data);
        this.searchService.saveSearchModelData(this.searchService.searchModelData.omit());
        this.locationFilterUpdateSearchActions(data);
    }
    changeCompanyBodyData(data) {
        this.requestCompaniesBody.next(data);
        this.searchService.saveSearchModelData(this.searchService.searchModelData.omit());
        this.locationFilterUpdateSearchActions(data);
    }

    changeCompanySaveSearch(data: SavedSearchCreateUpdateResponse<RequestBodyCompany>): void {
        this.saveSearchCompany.next(data);
        this.locationFilterUpdateSearchActions(data.query);
    }

    changeContactSaveSearch(data: SavedSearchCreateUpdateResponse<RequestBodyContact>): void {
        this.saveSearchContact.next(data);
        this.locationFilterUpdateSearchActions(data.query);
    }

    changeRevealMatchesID(matchsId) {
        this.revealMatchsId.next(matchsId);
    }
    changeCompanyRevealMatchesID(matchsId) {
        this.revealCompanyMatchsId.next(matchsId);
    }
    setValid(sidebarTab: SidebarTabTypeEnum, isValid) {
        if (sidebarTab === SidebarTabTypeEnum.SALES_INTEL) {
            this.isValid.next(isValid);
        } else if (sidebarTab === SidebarTabTypeEnum.VISITOR_INTEL) {
            this.isVIFilterValid.next(isValid);
        }
    }
    checkVIFilterChangeFirstTime() {
        if (this.isVIFilterChangeFirstTime.getValue()) {
            this.isVIFilterChangeFirstTime.next(false);
        }
    }
    getSaveSearchVisitorIntel() {
        return this.saveSearchVisitorIntel.asObservable();
    }
    setSaveSearchVisitorIntel(data) {
        this.saveSearchVisitorIntel.next(data);
    }

    getClearSearchVI(): Observable<void> {
        return this.clearSearchSidebarVI.asObservable();
    }

    emitClearSearchVI(): void {
        this.clearSearchSidebarVI.next();
    }

    getFiltersEditedEvent(): Observable<{ requestBody: SearchModelForm; edited: boolean }> {
        return this.filtersEdited.asObservable();
    }

    emitFiltersEditedEvent(requestBody: SearchModelForm, edited: boolean): void {
        this.filtersEdited.next({ requestBody, edited });
    }
}
